/**
 * C•CLUB auth modals.
 *
 * Purpose:
 * - Manage signup/signin/forgot-password overlays.
 * - Validate signup form input before API calls.
 * - Call custom app APIs for signup/signin/password reset.
 * - Persist lightweight auth/profile data for topbar/account overlays.
 */
(function () {
  // ---------------------------------------------------------------------------
  // Storage utilities
  // ---------------------------------------------------------------------------
  const storage = (function () {
    function store(useSession) {
      return useSession ? sessionStorage : localStorage;
    }

    return {
      get: function (key, useSession) {
        try {
          return store(!!useSession).getItem(key);
        } catch (_err) {
          return null;
        }
      },
      set: function (key, value, useSession) {
        try {
          store(!!useSession).setItem(key, value);
          return true;
        } catch (_err) {
          return false;
        }
      },
      remove: function (key, useSession) {
        try {
          store(!!useSession).removeItem(key);
        } catch (_err) {}
      },
    };
  })();

    // ---------------------------------------------------------------------------
    // Generic UI helpers
    // ---------------------------------------------------------------------------
    function setButtonLoading(button, isLoading, defaultText, loadingText) {
      if (!button) return;
      if (isLoading) {
        button.disabled = true;
        button.textContent = loadingText;
      } else {
        button.disabled = false;
        button.textContent = defaultText;
      }
    }
  
    function setFeedback(el, message, type) {
      if (!el) return;
      if (type == null) {
        el.hidden = true;
        el.textContent = '';
        el.classList.remove('signup-modal-feedback--success', 'signup-modal-feedback--error');
        return;
      }
      el.hidden = false;
      el.textContent = message || '';
      el.style.background = '';
      el.style.color = '';
      el.classList.remove('signup-modal-feedback--success', 'signup-modal-feedback--error');
      el.classList.add(type === 'error' ? 'signup-modal-feedback--error' : 'signup-modal-feedback--success');
    }

    // ---------------------------------------------------------------------------
    // API and validation helpers
    // ---------------------------------------------------------------------------
  function parseApiError(data) {
    if (!data || typeof data !== 'object') return null;
    if (typeof data.error === 'string' && data.error) return data.error;
    if (data.error && typeof data.error === 'object' && data.error.message)
      return String(data.error.message);
    if (Array.isArray(data.error_messages) && data.error_messages.length) {
      const first = data.error_messages[0];
      if (typeof first === 'string') return first;
      if (first && typeof first.message === 'string') return first.message;
    }
    if (typeof data.message === 'string' && data.message) return data.message;
    return null;
  }

  function isNonEmpty(value) {
    return String(value == null ? '' : value).trim() !== '';
  }

  function normalizeEmail(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function mapCountryToCode(country) {
    const c = String(country == null ? '' : country).trim().toLowerCase();
    if (!c) return '';
    if (c === 'us' || c === 'usa' || c === 'united states') return 'US';
    if (c === 'ca' || c === 'canada') return 'CA';
    return c.toUpperCase();
  }

  function toWixCountryName(country) {
    const c = String(country == null ? '' : country).trim().toLowerCase();
    if (!c || c === 'us' || c === 'usa' || c === 'united states') {
      return 'United States';
    }
    if (c === 'ca' || c === 'canada') {
      return 'Canada';
    }
    return String(country == null ? '' : country).trim();
  }

  function validateZipCode(zipcode, countryCode) {
    const zip = String(zipcode == null ? '' : zipcode).trim();
    // Optional field (Wix parity): validate only when user provides a value.
    if (!zip) return { ok: true };
    if (zip.length < 3 || zip.length > 10) {
      return { ok: false, message: 'Please enter a valid ZIP or postal code' };
    }
    if (countryCode === 'US') {
      if (!/^\d{5}(?:-\d{4})?$/.test(zip)) {
        return { ok: false, message: 'Please enter a valid US ZIP code' };
      }
    }
    if (countryCode === 'CA') {
      if (!/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(zip)) {
        return { ok: false, message: 'Please enter a valid Canadian postal code' };
      }
    }
    return { ok: true };
  }

  function parseDob(monthValue, dayValue, yearValue, countryCode) {
    const m = Number(monthValue);
    const d = Number(dayValue);
    const y = Number(yearValue);
    if (!Number.isInteger(m) || !Number.isInteger(d) || !Number.isInteger(y)) {
      return { ok: false, message: 'Date of birth is required' };
    }
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900) {
      return { ok: false, message: 'Please enter a valid date of birth' };
    }
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
      return { ok: false, message: 'Please enter a valid date of birth' };
    }
    // AB#618: hard floor is 13 (no one under 13 may register); once the country is
    // known the check is granular - 13 in the US, 14 in Canada (Quebec excluded).
    var minAge = String(countryCode || '').toUpperCase() === 'CA' ? 14 : 13;
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - minAge);
    minDate.setHours(0, 0, 0, 0);
    if (date > minDate) {
      var where =
        minAge === 14
          ? '14 years old in Canada (residents of Quebec are not eligible)'
          : '13 years old in the US';
      return { ok: false, message: 'You must be at least ' + where + ' to register.' };
    }
    return { ok: true };
  }

  // AB#618: make the DOB-eligibility "?" helper reliably toggle on click (the native
  // title showed nothing). Hover/focus are handled in CSS; this adds click + outside-close.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.signin-modal__dob-help') : null;
    var openTips = document.querySelectorAll('.dob-help-tip.is-open');
    var keep = btn ? btn.parentElement.querySelector('.dob-help-tip') : null;
    for (var i = 0; i < openTips.length; i++) {
      if (openTips[i] !== keep) openTips[i].classList.remove('is-open');
    }
    if (btn) {
      e.preventDefault();
      var tip = btn.parentElement.querySelector('.dob-help-tip');
      if (tip) tip.classList.toggle('is-open');
    }
  });

  function parseBooleanCheckbox(el) {
    return !!(el && el.checked);
  }

  function debugLogSignup(stage, data) {
    try {
      console.log('[cclub-signup][' + stage + ']', data);
    } catch (_err) {}
  }

  /**
   * Bind one popup mount instance.
   * Each section/snippet instance gets its own mount and handlers.
   */
    function bindMount(mount) {
      if (!mount || mount.getAttribute('data-cclub-iwt-bound') === '1') return;
      mount.setAttribute('data-cclub-iwt-bound', '1');

      const BASE_URL = (mount.getAttribute('data-backend') || '').trim();
      const SUCCESS_REDIRECT = (mount.getAttribute('data-success-redirect') || '').trim();
      const mountId = mount.id;

      function withPersonalInfoState(url) {
        const fallback = '/pages/my-account?state=personalInfoState';
        const raw = String(url || '').trim();
        if (!raw) return fallback;
        try {
          const parsed = new URL(raw, window.location.origin);
          if (!parsed.searchParams.get('state')) {
            parsed.searchParams.set('state', 'personalInfoState');
          }
          return parsed.pathname + parsed.search + parsed.hash;
        } catch (_err) {
          if (raw.indexOf('state=') >= 0) return raw;
          return raw + (raw.indexOf('?') >= 0 ? '&' : '?') + 'state=personalInfoState';
        }
      }

      function getApiUrl(path) {
        const base = BASE_URL.replace(/\/$/, '');
        const p = String(path || '').replace(/^\//, '');
        return base + '/' + p;
      }

      async function apiRequest(path, payload, errOpts) {
        errOpts = errOpts || {};
        const errFetch = errOpts.fetchError || 'Network error. Please try again.';
        const errParse = errOpts.parseError != null ? errOpts.parseError : errFetch;
        let res;
        try {
          res = await fetch(getApiUrl(path), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } catch (_err) {
          return { success: false, data: null, error: errFetch };
        }
        try {
          const data = await res.json();
          return { success: true, data, error: null, status: res.status };
        } catch (_err) {
          return { success: false, data: null, error: errParse };
        }
      }

      /**
       * Keep the profile overlay cache merged (session + local),
       * so account page can render data immediately after auth flows.
       */
      function persistSignupProfileOverlayMerged(incoming) {
        const KEY = 'signupProfileOverlay';
        try {
          let prev = {};
          try {
            const raw = storage.get(KEY, true) || storage.get(KEY, false);
            if (raw) prev = JSON.parse(raw) || {};
          } catch (_readErr) {}
          const out = Object.assign({}, prev);
          if (incoming && typeof incoming === 'object') {
            ['email', 'dob_month', 'dob_day', 'dob_year', 'country', 'zipcode'].forEach(function (k) {
              const v = incoming[k];
              if (v != null && String(v).trim() !== '') {
                out[k] =
                  k === 'country'
                    ? String(v).trim().toUpperCase()
                    : String(v).trim();
              }
            });
          }
          if (!out.email && prev.email) out.email = prev.email;
          const json = JSON.stringify(out);
          storage.set(KEY, json, true);
          storage.set(KEY, json, false);
        } catch (_persistErr) {}
      }

      // Main element references for this mount.
      const signupOverlay = mount.querySelector('[data-iwt="signup-overlay"]');
      const signinOverlay = mount.querySelector('[data-iwt="signin-overlay"]');
      const signupForm = mount.querySelector('[data-iwt="signup-form"]');
      const signinForm = mount.querySelector('[data-iwt="signin-form"]');
      const signupFeedback = mount.querySelector('[data-iwt="signup-feedback"]');
      const signinFeedback = mount.querySelector('[data-iwt="signin-feedback"]');
      const signupSubmit = mount.querySelector('[data-iwt="signup-submit"]');
      const signinSubmit = mount.querySelector('[data-iwt="signin-submit"]');
      const signupEmailOptin = signupForm ? signupForm.querySelector('[data-iwt="email-optin"]') : null;
      const signupSmsOptin = signupForm ? signupForm.querySelector('[data-iwt="sms-optin"]') : null;
      // AB#544 — Join C-CLUB checkbox (default unchecked). Value flows into AB#452c passwordless payload as `loyaltyMember`.
      const signupLoyaltyMember = signupForm ? signupForm.querySelector('[data-iwt="loyalty-member-optin"]') : null;
      const signinEmailInput = signinForm ? signinForm.querySelector('[name="email"]') : null;
      const signinPasswordInput = signinForm ? signinForm.querySelector('[name="password"]') : null;
      const gotoSignupBtn = mount.querySelector('[data-iwt="goto-signup"]');
      const forgotPasswordLink = mount.querySelector('[data-iwt="forgot-password-link"]');
      const forgotModal = mount.querySelector('[data-iwt="forgot-modal"]');
      const forgotEmailInput = mount.querySelector('[data-iwt="forgot-email-input"]');
      const forgotSubmitBtn = mount.querySelector('[data-iwt="forgot-submit-btn"]');
      const forgotCloseBtn = mount.querySelector('[data-iwt="forgot-close-btn"]');
      const forgotModalMessage = mount.querySelector('[data-iwt="forgot-modal-message"]');
      const regCol = mount.querySelector('[data-iwt="register-col"]');

      let scrollY = 0;

      // Place the common signup feedback message directly above submit area.
      if (signupForm && signupFeedback) {
        const submitWrap = signupForm.querySelector('.signup-modal-submit-wrap');
        if (submitWrap && signupFeedback.parentElement !== signupForm) {
          signupForm.insertBefore(signupFeedback, submitWrap);
        } else if (submitWrap) {
          signupForm.insertBefore(signupFeedback, submitWrap);
        }
      }

      /**
       * Signup inline field errors (Wix-like):
       * - Render/maintain one error line under each signup field group.
       * - Show all validation errors together on submit.
       */
      function getSignupFieldErrorEl(fieldKey) {
        if (!signupForm) return null;
        const existing = signupForm.querySelector(
          '[data-iwt-field-error="' + fieldKey + '"]'
        );
        if (existing) return existing;

        const fieldWrap = signupForm.querySelector('[data-field="' + fieldKey + '"]');
        if (!fieldWrap) return null;

        const p = document.createElement('p');
        p.className = 'signup-modal-field-error';
        p.setAttribute('data-iwt-field-error', fieldKey);
        p.setAttribute('role', 'alert');
        p.hidden = true;
        fieldWrap.appendChild(p);
        return p;
      }

      function clearSignupFieldError(fieldKey) {
        const el = getSignupFieldErrorEl(fieldKey);
        if (!el) return;
        el.hidden = true;
        el.textContent = '';
      }

      function setSignupFieldError(fieldKey, message) {
        const el = getSignupFieldErrorEl(fieldKey);
        if (!el) return;
        el.hidden = !message;
        el.textContent = message || '';
      }

      function clearAllSignupFieldErrors() {
        [
          'first_name',
          'last_name',
          'email',
          'dob',
          'country',
          'zipcode',
          'phone',
          'password',
          'password_confirm',
        ].forEach(clearSignupFieldError);
      }

      function getSigninFieldErrorEl(fieldKey) {
        if (!signinForm) return null;
        const existing = signinForm.querySelector(
          '[data-iwt-signin-field-error="' + fieldKey + '"]'
        );
        if (existing) return existing;
        const fieldWrap = signinForm.querySelector('[data-field="' + fieldKey + '"]');
        if (!fieldWrap) return null;
        const p = document.createElement('p');
        p.className = 'signup-modal-field-error';
        p.setAttribute('data-iwt-signin-field-error', fieldKey);
        p.setAttribute('role', 'alert');
        p.hidden = true;
        fieldWrap.appendChild(p);
        return p;
      }

      function setSigninFieldError(fieldKey, message) {
        const el = getSigninFieldErrorEl(fieldKey);
        if (!el) return;
        el.hidden = !message;
        el.textContent = message || '';
      }

      function clearAllSigninFieldErrors() {
        ['signin_email', 'signin_password'].forEach(function (k) {
          setSigninFieldError(k, '');
        });
      }

      function showExistingMemberSigninMessage() {
        if (!signupFeedback) return;
        signupFeedback.hidden = false;
        signupFeedback.classList.remove(
          'signup-modal-feedback--success',
          'signup-modal-feedback--error'
        );
        signupFeedback.classList.add('signup-modal-feedback--error');
        signupFeedback.innerHTML =
          'Customer email is already listed as a member. Please sign in <a href="#" data-iwt="signup-duplicate-signin-link">HERE</a>.';
        const link = signupFeedback.querySelector(
          '[data-iwt="signup-duplicate-signin-link"]'
        );
        if (link) {
          link.addEventListener('click', function (event) {
            event.preventDefault();
            openSignin();
          });
        }
      }
  
      // -------------------------------------------------------------------------
      // Forgot password modal
      // -------------------------------------------------------------------------
      function showForgotMessage(text, isErr) {
        if (!forgotModalMessage) return;
        forgotModalMessage.removeAttribute('hidden');
        forgotModalMessage.textContent = text || '';
        forgotModalMessage.classList.remove('forgot-modal__message--error', 'forgot-modal__message--ok');
        forgotModalMessage.classList.add(isErr ? 'forgot-modal__message--error' : 'forgot-modal__message--ok');
      }
  
      function hideForgotMessage() {
        if (!forgotModalMessage) return;
        forgotModalMessage.setAttribute('hidden', '');
        forgotModalMessage.textContent = '';
        forgotModalMessage.classList.remove('forgot-modal__message--error', 'forgot-modal__message--ok');
      }
  
      function closeForgotModal() {
        if (!forgotModal) return;
        forgotModal.classList.add('hidden');
        forgotModal.setAttribute('aria-hidden', 'true');
        hideForgotMessage();
        if (forgotEmailInput) forgotEmailInput.value = '';
        if (forgotSubmitBtn) {
          forgotSubmitBtn.disabled = false;
          const fd = forgotSubmitBtn.getAttribute('data-default-label');
          if (fd) forgotSubmitBtn.textContent = fd;
        }
      }
  
      function openForgotModal() {
        if (!forgotModal) return;
        hideForgotMessage();
        if (forgotEmailInput) forgotEmailInput.value = '';
        forgotModal.classList.remove('hidden');
        forgotModal.setAttribute('aria-hidden', 'false');
        setTimeout(function () {
          if (forgotEmailInput) forgotEmailInput.focus();
        }, 0);
      }
  
      // -------------------------------------------------------------------------
      // Overlay open/close + body scroll lock
      // -------------------------------------------------------------------------
      function lockBody() {
        scrollY = window.scrollY || document.documentElement.scrollTop;
        document.body.style.position = 'fixed';
        document.body.style.top = '-' + scrollY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
      }
  
      function unlockBody() {
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('left');
        document.body.style.removeProperty('right');
        document.body.style.removeProperty('overflow');
        window.scrollTo(0, scrollY);
      }
  
      function openSignup() {
        if (signinOverlay) signinOverlay.classList.add('hidden');
        if (signupOverlay) {
          signupOverlay.classList.remove('hidden');
          signupOverlay.setAttribute('aria-hidden', 'false');
        }
        lockBody();
        setFeedback(signupFeedback, '', null);
        // AB#500b: clear the AB#497b top banner so JOIN NOW path doesn't
        // inherit the "not a member yet" message from a prior lookup flow.
        setFeedback(mount.querySelector('[data-iwt="signup-top-feedback"]'), '', null);
      }
  
      function openSignin() {
        if (signupOverlay) signupOverlay.classList.add('hidden');
        if (signinOverlay) {
          signinOverlay.classList.remove('hidden');
          signinOverlay.setAttribute('aria-hidden', 'false');
        }
        lockBody();
        setFeedback(signinFeedback, '', null);
      }
  
      function closeAll() {
        closeForgotModal();
        if (signupOverlay) {
          signupOverlay.classList.add('hidden');
          signupOverlay.setAttribute('aria-hidden', 'true');
        }
        if (signinOverlay) {
          signinOverlay.classList.add('hidden');
          signinOverlay.setAttribute('aria-hidden', 'true');
        }
        unlockBody();
      }
  
      // Registry used by global click handlers and legacy custom events.
      window.__cclubIwtOpens = window.__cclubIwtOpens || {};
      window.__cclubIwtOpens[mountId] = {
        openSignup: openSignup,
        openSignin: openSignin,
        closeAll: closeAll,
        closeForgotModal: closeForgotModal,
        mount: mount,
      };
  
      // -------------------------------------------------------------------------
      // Topbar auth button + greeting state
      // -------------------------------------------------------------------------
      const topbarOpenBtn = mount.querySelector('[data-cclub-auth-topbar-open]');
      if (topbarOpenBtn) {
        topbarOpenBtn.addEventListener('click', function (e) {
          e.preventDefault();
          const k = (topbarOpenBtn.getAttribute('data-cclub-auth-topbar-open') || 'signup').toLowerCase();
          if (k === 'signin') openSignin();
          else openSignup();
        });
      }
  
      const authActions = mount.querySelector('[data-cclub-auth-actions]');
      const authState = mount.querySelector('[data-cclub-auth-state]');
      const authGreeting = mount.querySelector('[data-cclub-auth-greeting]');
      const authLogoutBtn = mount.querySelector('[data-cclub-auth-logout]');
  
      function getStoredAccessToken() {
        return storage.get('customerAccessToken') || '';
      }
      function getStoredTokenExpiresAt() {
        return storage.get('customerAccessTokenExpiresAt') || '';
      }
      function clearCustomerAuthStorage() {
        storage.remove('customerAccessToken');
        storage.remove('customerAccessTokenExpiresAt');
        storage.remove('customerData');
      }
      function tokenExpired(expiresAt) {
        if (!expiresAt) return false;
        const ms = Date.parse(String(expiresAt));
        if (!ms || isNaN(ms)) return false;
        return ms <= Date.now();
      }
      function setTopbarLoggedOut() {
        if (authActions) authActions.classList.remove('hidden');
        if (authState) authState.classList.add('hidden');
        if (authGreeting) authGreeting.textContent = 'Hi';
      }
      function setTopbarLoggedIn(firstName) {
        if (authActions) authActions.classList.add('hidden');
        if (authState) authState.classList.remove('hidden');
        const fn = firstName && String(firstName).trim() ? String(firstName).trim() : '';
        if (authGreeting) authGreeting.textContent = fn ? 'Hi, ' + fn : 'Hi';
      }
      function restoreAuthTopbar() {
        if (!authState) return;
        const token = getStoredAccessToken();
        if (!token) {
          setTopbarLoggedOut();
          return;
        }
        const exp = getStoredTokenExpiresAt();
        if (tokenExpired(exp)) {
          clearCustomerAuthStorage();
          setTopbarLoggedOut();
          return;
        }
        try {
          const raw = storage.get('customerData');
          const cust = raw ? JSON.parse(raw) : null;
          setTopbarLoggedIn(cust && cust.firstName);
        } catch (e) {
          setTopbarLoggedIn(null);
        }
      }
      if (authLogoutBtn) {
        authLogoutBtn.addEventListener('click', function () {
          clearCustomerAuthStorage();
          try {
            window.location.reload();
          } catch (_err) {}
        });
      }
      restoreAuthTopbar();
  
      mount.addEventListener('click', function (e) {
        if (e.target.closest('[data-iwt="signup-close"]') || e.target.closest('[data-iwt="signup-backdrop"]')) {
          e.preventDefault();
          closeAll();
        }
        if (e.target.closest('[data-iwt="signin-close"]') || e.target.closest('[data-iwt="signin-backdrop"]')) {
          e.preventDefault();
          closeAll();
        }
        if (e.target.closest('[data-iwt="forgot-modal-backdrop"]')) {
          e.preventDefault();
          closeForgotModal();
        }
      });
  
      if (forgotPasswordLink) {
        forgotPasswordLink.addEventListener('click', function (e) {
          e.preventDefault();
          openForgotModal();
        });
      }
  
      if (forgotCloseBtn) {
        forgotCloseBtn.addEventListener('click', function () {
          closeForgotModal();
        });
      }
  
      // AB#440: NCA OTP is the password recovery — there is no separate "forgot password" anymore.
      // Redirect to /auth/login with the email prefilled; Shopify sends the magic link.
      if (forgotSubmitBtn && forgotEmailInput) {
        const forgotSubmitDefault = forgotSubmitBtn.getAttribute('data-default-label') || forgotSubmitBtn.textContent || '';
        forgotSubmitBtn.addEventListener('click', function () {
          hideForgotMessage();
          if (!BASE_URL) {
            showForgotMessage('Sign-in is not configured (missing backend URL).', true);
            return;
          }
          const email = String(forgotEmailInput.value || '').trim();
          if (!email) {
            showForgotMessage('Please enter your email address.', true);
            forgotEmailInput.focus();
            return;
          }
          setButtonLoading(forgotSubmitBtn, true, forgotSubmitDefault, 'Sending…');
          // AB#444: post-signin land on the account profile page, not the calling page
          const returnTo = withPersonalInfoState(SUCCESS_REDIRECT);
          // AB#446: force_login=1 makes /auth/login bounce through Shopify logout first.
          const authUrl =
            BASE_URL.replace(/\/$/, '') +
            '/auth/login?email=' + encodeURIComponent(email) +
            '&return_to=' + encodeURIComponent(returnTo) +
            '&force_login=1' +
            '&shop_origin=' + encodeURIComponent(window.location.origin);
          window.location.href = authUrl;
        });
      }
  
      // -------------------------------------------------------------------------
      // DOB utilities (signin register panel + signup form)
      // -------------------------------------------------------------------------
      function fillDobSelects(scope, maxYear, minYear) {
        const dm = scope.querySelector('[name="dob_month"]');
        const dd = scope.querySelector('[name="dob_day"]');
        const dy = scope.querySelector('[name="dob_year"]');
        if (dm && dm.options.length <= 1) {
          for (let m = 1; m <= 12; m++) {
            const mv = (m < 10 ? '0' : '') + m;
            const o = document.createElement('option');
            o.value = String(m);
            o.textContent = mv;
            dm.appendChild(o);
          }
        }
        if (dd && dd.options.length <= 1) {
          for (let d = 1; d <= 31; d++) {
            const dv = (d < 10 ? '0' : '') + d;
            const o2 = document.createElement('option');
            o2.value = String(d);
            o2.textContent = dv;
            dd.appendChild(o2);
          }
        }
        if (dy && dy.options.length <= 1) {
          for (let y = maxYear; y >= minYear; y--) {
            const o3 = document.createElement('option');
            o3.value = String(y);
            o3.textContent = String(y);
            dy.appendChild(o3);
          }
        }
      }
  
      // Wix parity: year list starts at (current year - 13) and goes back 200 years.
      const currentYear = new Date().getFullYear();
      const dobMaxYear = currentYear - 13;
      const dobMinYear = currentYear - 200;
      if (signupForm) fillDobSelects(signupForm, dobMaxYear, dobMinYear);
      if (regCol) fillDobSelects(regCol, dobMaxYear, dobMinYear);
  
      const dobErr = mount.querySelector('[data-iwt="signin-dob-err"]');
      if (gotoSignupBtn && signupForm) {
        gotoSignupBtn.addEventListener('click', function () {
          const dm = regCol ? regCol.querySelector('[name="dob_month"]') : null;
          const dd = regCol ? regCol.querySelector('[name="dob_day"]') : null;
          const dy = regCol ? regCol.querySelector('[name="dob_year"]') : null;
          if (dobErr) dobErr.textContent = '';
          if (!dm || !dd || !dy || !dm.value || !dd.value || !dy.value) {
            if (dobErr) dobErr.textContent = 'Please select your full date of birth.';
            return;
          }
          const sm = signupForm.querySelector('[name="dob_month"]');
          const sd = signupForm.querySelector('[name="dob_day"]');
          const sy = signupForm.querySelector('[name="dob_year"]');
          if (sm) sm.value = dm.value;
          if (sd) sd.value = dd.value;
          if (sy) sy.value = dy.value;
          openSignup();
        });
      }
  
      // -------------------------------------------------------------------------
      // Signup submit flow
      // -------------------------------------------------------------------------
      if (signupForm && signupSubmit) {
        const subDefault = signupSubmit.getAttribute('data-default-label') || signupSubmit.textContent || '';

        // Create field-error placeholders once and clear inline errors while typing.
        clearAllSignupFieldErrors();
        signupForm.addEventListener('input', function (event) {
          const target = event.target;
          if (!target || !target.name) return;
          const fieldMap = {
            first_name: 'first_name',
            last_name: 'last_name',
            email: 'email',
            dob_month: 'dob',
            dob_day: 'dob',
            dob_year: 'dob',
            country: 'country',
            zipcode: 'zipcode',
            phone: 'phone',
            password: 'password',
            password_confirm: 'password_confirm',
          };
          const mapped = fieldMap[target.name];
          if (mapped) clearSignupFieldError(mapped);
        });

        signupForm.addEventListener('submit', async function (ev) {
          ev.preventDefault();
          setFeedback(signupFeedback, '', null);
          clearAllSignupFieldErrors();
          // Keep button label as SIGN UP while request is in-flight.
          setButtonLoading(signupSubmit, true, subDefault, subDefault);
  
          const fd = new FormData(signupForm);
          const rawFormData = {};
          fd.forEach(function (value, key) {
            rawFormData[key] = value;
          });
          debugLogSignup('raw-form-data', rawFormData);

          const phoneCode = (fd.get('phone_code') || '+1').toString();
          const phoneRaw = (fd.get('phone') || '').toString();
          const digits = phoneRaw.replace(/\D/g, '');
          const dobMonth = (fd.get('dob_month') || '').toString();
          const dobDay = (fd.get('dob_day') || '').toString();
          const dobYear = (fd.get('dob_year') || '').toString();
  
          const email = normalizeEmail(fd.get('email'));
          const password = (fd.get('password') || '').toString();
          const confirmPassword = (fd.get('password_confirm') || '').toString();
          const firstName = (fd.get('first_name') || '').toString().trim();
          const lastName = (fd.get('last_name') || '').toString().trim();
          const country = (fd.get('country') || '').toString().trim();
          const wixCountry = toWixCountryName(country);
          const countryCode = mapCountryToCode(country);
          const zipcode = (fd.get('zipcode') || '').toString().trim();

          debugLogSignup('normalized-inputs', {
            email,
            firstName,
            lastName,
            country,
            wixCountry,
            countryCode,
            zipcode,
            dobMonth,
            dobDay,
            dobYear,
            phoneCode,
            phoneRaw,
            phoneDigits: digits,
          });

          const emailInput = signupForm.querySelector('[name="email"]');
          const passwordInput = signupForm.querySelector('[name="password"]');
          const confirmPasswordInput = signupForm.querySelector('[name="password_confirm"]');
          const firstNameInput = signupForm.querySelector('[name="first_name"]');
          const lastNameInput = signupForm.querySelector('[name="last_name"]');
          const countryInput = signupForm.querySelector('[name="country"]');
          const zipcodeInput = signupForm.querySelector('[name="zipcode"]');
          const dobMonthInput = signupForm.querySelector('[name="dob_month"]');
          const phoneInput = signupForm.querySelector('[name="phone"]');

          /** @type {Record<string, string>} */
          const validationErrors = {};

          if (!isNonEmpty(firstName)) {
            validationErrors.first_name = 'First name is required.';
          }
          if (!isNonEmpty(lastName)) {
            validationErrors.last_name = 'Last name is required.';
          }
          if (!isNonEmpty(email)) {
            validationErrors.email = 'Email address is required.';
          } else if (!isValidEmail(email)) {
            validationErrors.email =
              'Please enter a valid email address (e.g., name@example.com).';
          }
          // AB#452b: password validation removed; signup is passwordless (NCA OTP magic-link).
          if (!isNonEmpty(countryCode)) {
            validationErrors.country = 'Country selection is required.';
          }
          const zipValidation = validateZipCode(zipcode, countryCode);
          if (!zipValidation.ok) {
            validationErrors.zipcode = zipValidation.message;
          }
          if (!isNonEmpty(dobMonth) || !isNonEmpty(dobDay) || !isNonEmpty(dobYear)) {
            validationErrors.dob = 'Date of birth is required.';
          } else {
            const dobValidation = parseDob(dobMonth, dobDay, dobYear, countryCode);
            if (!dobValidation.ok) {
              validationErrors.dob = dobValidation.message;
            }
          }
          // Optional phone (Wix parity): validate only when user enters one.
          if (digits && (digits.length < 7 || digits.length > 15)) {
            validationErrors.phone =
              'Please enter a valid country code and phone number.';
          }

          const errorKeys = Object.keys(validationErrors);
          if (errorKeys.length > 0) {
            errorKeys.forEach(function (key) {
              setSignupFieldError(key, validationErrors[key]);
            });

            debugLogSignup('validation-failed', validationErrors);
            setFeedback(signupFeedback, 'Input error. Please check the form fields.', 'error');
            setButtonLoading(signupSubmit, false, subDefault, 'Please wait…');

            // Focus first invalid field for accessibility.
            const firstField =
              (validationErrors.first_name && firstNameInput) ||
              (validationErrors.last_name && lastNameInput) ||
              (validationErrors.email && emailInput) ||
              (validationErrors.country && countryInput) ||
              (validationErrors.zipcode && zipcodeInput) ||
              (validationErrors.dob && dobMonthInput) ||
              (validationErrors.phone && phoneInput) ||
              null;
            if (firstField && typeof firstField.focus === 'function') firstField.focus();
            return;
          }

          const emailOptedIn = parseBooleanCheckbox(signupEmailOptin);
          const smsOptedIn = parseBooleanCheckbox(signupSmsOptin);
          // AB#544 — read Join C-CLUB checkbox state.
          const loyaltyMemberOptedIn = parseBooleanCheckbox(signupLoyaltyMember);
          // AB#900: Join C-CLUB is required to submit (footer newsletter covers email-only signups).
          if (!loyaltyMemberOptedIn) {
            setFeedback(signupFeedback, "Please check Join C-CLUB and start earning rewards to sign up.", "error");
            setButtonLoading(signupSubmit, false, subDefault, "Please wait...");
            if (signupLoyaltyMember && typeof signupLoyaltyMember.focus === "function") signupLoyaltyMember.focus();
            return;
          }

          const payload = {
            email: email,
            password: password,
            confirm_password: confirmPassword,
            first_name: firstName,
            last_name: lastName,
            phone:
              digits.length > 0
                ? String(phoneCode).replace(/\s/g, '') + digits
                : '',
            country_code: String(phoneCode || '+1').trim() || '+1',
            postal_code: zipcode,
            zipcode: zipcode,
            country: wixCountry,
            dob_day: dobDay,
            dob_month: dobMonth,
            dob_year: dobYear,
            birth_day: dobDay,
            birth_month: dobMonth,
            birth_year: dobYear,
            signUpForEmailUpdate: emailOptedIn,
            autodialMarketingMessage: smsOptedIn,
            email_optin: emailOptedIn ? 1 : 0,
            acceptsMarketing: emailOptedIn ? 1 : 0,
            sms_optin: smsOptedIn ? 1 : 0,
            accepts_marketing: emailOptedIn ? 1 : 0,
          };

          debugLogSignup('payload-before-api', payload);
  
          // AB#452c: passwordless signup. POST /api/signup-passwordless (AB#342) ->
          // Inteq enroll + Shopify customer create -> redirect to /auth/login for OTP
          // magic-link. Bypasses the legacy /api/signup + /api/signin password flow.
          var phoneDigitsAB452c = digits || '';
          var phoneCcDigitsAB452c = String(phoneCode || '+1').replace(/[^0-9]/g, '') || '1';
          var passwordlessPayloadAB452c = {
            email: email,
            firstName: firstName,
            lastName: lastName,
            phone: phoneDigitsAB452c,
            phoneCountryCode: phoneCcDigitsAB452c,
            birthYear: String(dobYear || ''),
            birthMonth: String(dobMonth || ''),
            birthDay: String(dobDay || ''),
            postalCode: String(zipcode || ''),
            country: String(countryCode || 'US'),
            emailOptIn: !!emailOptedIn,
            smsOptIn: !!smsOptedIn,
            loyaltyMember: !!loyaltyMemberOptedIn,
          };
          debugLogSignup('payload-AB452c-passwordless', passwordlessPayloadAB452c);
          var pwlessRespAB452c;
          try {
            pwlessRespAB452c = await fetch(BASE_URL.replace(/\/$/, '') + '/api/signup-passwordless', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(passwordlessPayloadAB452c),
            });
          } catch (netErrAB452c) {
            setFeedback(signupFeedback, 'Network error during sign-up. Please try again.', 'error');
            setButtonLoading(signupSubmit, false, subDefault, 'Please wait...');
            return;
          }
          var pwlessJsonAB452c = null;
          try { pwlessJsonAB452c = await pwlessRespAB452c.json(); } catch (_pe) { pwlessJsonAB452c = null; }
          debugLogSignup('signup-passwordless-response', { status: pwlessRespAB452c.status, body: pwlessJsonAB452c });
          if ((pwlessRespAB452c.status === 200 && pwlessJsonAB452c && pwlessJsonAB452c.success) || pwlessRespAB452c.status === 409) {
            var returnToAB452c = withPersonalInfoState(SUCCESS_REDIRECT || '/pages/my-account');
            var authUrlAB452c =
              BASE_URL.replace(/\/$/, '') +
              '/auth/login?email=' + encodeURIComponent(email) +
              '&return_to=' + encodeURIComponent(returnToAB452c) +
              '&force_login=1' +
            '&shop_origin=' + encodeURIComponent(window.location.origin);
            setFeedback(signupFeedback, 'Account created! Sending you a sign-in link...', 'success');
            window.location.href = authUrlAB452c;
            return;
          }
          var pwlessErrMsgAB452c =
            (pwlessJsonAB452c && pwlessJsonAB452c.error && pwlessJsonAB452c.error.message) ||
            'Sign-up failed. Please try again.';
          setFeedback(signupFeedback, pwlessErrMsgAB452c, 'error');
          setButtonLoading(signupSubmit, false, subDefault, 'Please wait...');
          return;
          // -- Below is legacy /api/signup + /api/signin flow, now unreachable. Kept
          //    for diff clarity; can be removed in a follow-up cleanup.
          const signupResult = await apiRequest('api/signup', payload);
          debugLogSignup('signup-api-response', signupResult);
          if (!signupResult.success) {
            setFeedback(signupFeedback, signupResult.error, 'error');
            setButtonLoading(signupSubmit, false, subDefault, 'Please wait…');
            return;
          }
          const data = signupResult.data;
  
          if (data.success) {
            try {
              const sp = data.signupProfile;
              if (sp && typeof sp === 'object') {
                persistSignupProfileOverlayMerged(sp);
              } else {
                persistSignupProfileOverlayMerged({
                  email: String(payload.email || '')
                    .trim()
                    .toLowerCase(),
                  dob_month: String(dobMonth || '').trim(),
                  dob_day: String(dobDay || '').trim(),
                  dob_year: String(dobYear || '').trim(),
                  country: String(payload.country || '')
                    .trim()
                    .toUpperCase(),
                  zipcode: String(payload.zipcode || '').trim(),
                });
              }
            } catch (_overlayErr) {}
  
            const signinResult = await apiRequest(
              'api/signin',
              { email: payload.email, password: payload.password },
              {
                fetchError: 'Account created, but we could not sign you in. Please use Sign in.',
                parseError: 'Account created, but sign-in failed. Please use Sign in.',
              }
            );
            debugLogSignup('signin-after-signup-response', signinResult);
            if (!signinResult.success) {
              setFeedback(signupFeedback, signinResult.error, 'error');
              setButtonLoading(signupSubmit, false, subDefault, 'Please wait…');
              return;
            }
            const signinData = signinResult.data;
            if (!signinData.success || !signinData.accessToken) {
              setFeedback(
                signupFeedback,
                'Account created. ' + (parseApiError(signinData) || 'Please sign in with your new email and password.'),
                'error'
              );
              setButtonLoading(signupSubmit, false, subDefault, 'Please wait…');
              return;
            }
            try {
              if (signinData.signupProfile && typeof signinData.signupProfile === 'object') {
                persistSignupProfileOverlayMerged(signinData.signupProfile);
              }
            } catch (_pe2) {}
            storage.set('customerAccessToken', signinData.accessToken);
            if (signinData.expiresAt) storage.set('customerAccessTokenExpiresAt', String(signinData.expiresAt));
            const redirectTo = withPersonalInfoState(SUCCESS_REDIRECT || '/pages/my-account');
            var seconds = 5;
            setFeedback(
              signupFeedback,
              'Account created! Redirecting to your account page in ' + seconds + ' seconds…',
              'success'
            );
            var countdownTimer = setInterval(function () {
              seconds -= 1;
              if (seconds <= 0) {
                clearInterval(countdownTimer);
                window.location.href = redirectTo;
                return;
              }
              setFeedback(
                signupFeedback,
                'Account created! Redirecting to your account page in ' + seconds + ' seconds…',
                'success'
              );
            }, 1000);
          } else {
            const duplicateMember =
              data &&
              (data.existingMember === true ||
                signupResult.status === 409 ||
                /already listed as a member/i.test(String(data.error || '')));
            if (duplicateMember) {
              showExistingMemberSigninMessage();
            } else {
              setFeedback(
                signupFeedback,
                parseApiError(data) || data.error || 'Signup failed',
                'error'
              );
            }
            setButtonLoading(signupSubmit, false, subDefault, 'Please wait…');
          }
        });
      }
  
      // -------------------------------------------------------------------------
      // Signin submit flow
      // -------------------------------------------------------------------------
      // AB#440: Sign-in now redirects to Customer Account API OTP flow.
      // No password posted to /api/signin; magic link is sent from Shopify NCA hosted page.
      if (signinForm && signinSubmit) {
        const sigDefault = signinSubmit.getAttribute('data-default-label') || signinSubmit.textContent || '';
        signinForm.addEventListener('submit', function (ev) {
          ev.preventDefault();
          clearAllSigninFieldErrors();
          setFeedback(signinFeedback, '', null);

          const signinEmailRaw = signinEmailInput ? signinEmailInput.value : '';
          const signinEmail = String(signinEmailRaw || '').trim();
          if (!signinEmail) {
            setSigninFieldError('signin_email', 'Email is required.');
            return;
          }
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signinEmail)) {
            setSigninFieldError('signin_email', 'Please enter a valid email address.');
            return;
          }
          if (!BASE_URL) {
            setFeedback(signinFeedback, 'Sign-in is not configured (missing backend URL).', 'error');
            return;
          }

          setButtonLoading(signinSubmit, true, sigDefault, sigDefault);
          // AB#444: post-signin land on the account profile page, not the calling page
          const returnTo = withPersonalInfoState(SUCCESS_REDIRECT);

          // AB#452: Inteq pre-flight gate (was stripped by AB#440 NCA cutover). Before
          // sending the user to Shopify NCA OTP, confirm the email exists in Inteq. If
          // not enrolled, flip the popup to the signup overlay with email prefilled so
          // the user completes enrollment via api.signup-passwordless (AB#342) and gets
          // sent through OTP from the signup-success path.
          fetch(BASE_URL.replace(/\/$/, '') + '/api/lookup-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: signinEmail }),
          })
            .then(function (r) { return r.ok ? r.json() : { success: false, exists: false }; })
            .catch(function () { return { success: false, exists: false, error: 'network' }; })
            .then(function (lookup) {
              if (lookup && lookup.exists) {
                // Inteq member -- send through Shopify NCA OTP.
                // AB#446: force_login=1 bounces through Shopify logout first so the OTP
                // page is shown for this email instead of silently auto-auth.
                const authUrl =
                  BASE_URL.replace(/\/$/, '') +
                  '/auth/login?email=' + encodeURIComponent(signinEmail) +
                  '&return_to=' + encodeURIComponent(returnTo) +
                  '&force_login=1' +
            '&shop_origin=' + encodeURIComponent(window.location.origin);
                window.location.href = authUrl;
              } else {
                // Not enrolled in Inteq -- swap to signup overlay with email prefilled.
                setButtonLoading(signinSubmit, false, sigDefault, sigDefault);
                if (signupForm) {
                  var signupEmailField = signupForm.querySelector('[name="email"]');
                  if (signupEmailField) {
                    signupEmailField.value = signinEmail;
                    signupEmailField.readOnly = true; // prevent edit -- user can re-click signin to change
                  }
                }
                setFeedback(signinFeedback, '', null);
                openSignup();
                // AB#500: show "not a member yet" ONLY in the top banner (AB#497b).
                // The original in-form .signup-modal-feedback write was a duplicate.
                setFeedback(
                  mount.querySelector('[data-iwt="signup-top-feedback"]'),
                  'Looks like you are not a member yet. Complete the form below to join the family.',
                  'info'
                );
              }
            });
        });
      }
    }
  
    function mountRegistryOpenSignup(preferredId, sectionId) {
      const o = window.__cclubIwtOpens;
      if (!o) return false;
      if (preferredId && o[preferredId]) {
        o[preferredId].openSignup();
        return true;
      }
      if (sectionId != null) {
        const mid = 'cclub-iwt-mount-' + String(sectionId);
        if (o[mid]) {
          o[mid].openSignup();
          return true;
        }
      }
      const mounts = document.querySelectorAll('.cclub-iwt-mount');
      for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i];
        if (!el.querySelector('[data-iwt="signup-overlay"]')) continue;
        const id = el.id;
        if (id && o[id]) {
          o[id].openSignup();
          return true;
        }
      }
      return false;
    }
  
    function mountRegistryOpenSignin(preferredId, sectionId) {
      const o = window.__cclubIwtOpens;
      if (!o) return false;
      if (preferredId && o[preferredId]) {
        o[preferredId].openSignin();
        return true;
      }
      if (sectionId != null) {
        const mid = 'cclub-iwt-mount-' + String(sectionId);
        if (o[mid]) {
          o[mid].openSignin();
          return true;
        }
      }
      const mounts = document.querySelectorAll('.cclub-iwt-mount');
      for (let j = 0; j < mounts.length; j++) {
        const el2 = mounts[j];
        if (!el2.querySelector('[data-iwt="signin-overlay"]')) continue;
        const id2 = el2.id;
        if (id2 && o[id2]) {
          o[id2].openSignin();
          return true;
        }
      }
      return false;
    }
  
    function wireLegacyDispatchEventsOnce() {
      if (window.__cclubLegacyAuthEvents) return;
      window.__cclubLegacyAuthEvents = true;
      document.addEventListener('customeraccount:openSignupModal', function (e) {
        const d = (e && e.detail) || {};
        if (mountRegistryOpenSignup(d.mountId, d.sectionId)) return;
        if (document.querySelector('[data-cclub-auth-topbar-open="signup"]')) return;
        mountRegistryOpenSignin(d.mountId, d.sectionId);
      });
      document.addEventListener('customeraccount:openSigninModal', function (e) {
        const d = (e && e.detail) || {};
        mountRegistryOpenSignin(d.mountId, d.sectionId);
      });
    }
  
    function resolveMountIdFromEl(el) {
      if (!el) return '';
      return (
        el.getAttribute('data-cclub-mount') ||
        el.getAttribute('data-cclub-iwt-mount') ||
        ''
      ).trim();
    }
  
    function wireGlobalClickOnce() {
      if (window.__cclubIwtGlobalClick) return;
      window.__cclubIwtGlobalClick = true;
      document.addEventListener(
        'click',
        function (e) {
          const t = e.target.closest('[data-cclub-iwt-open][data-cclub-iwt-mount]');
          if (t) {
            const mid = t.getAttribute('data-cclub-iwt-mount');
            const kind = t.getAttribute('data-cclub-iwt-open');
            const reg = window.__cclubIwtOpens && window.__cclubIwtOpens[mid];
            if (!reg) return;
            e.preventDefault();
            if (kind === 'signup') reg.openSignup();
            else if (kind === 'signin') reg.openSignin();
            return;
          }
  
          const openAuthBtn = e.target.closest('[data-open-auth]');
          if (openAuthBtn) {
            e.preventDefault();
            const authKind = (openAuthBtn.getAttribute('data-open-auth') || '').toLowerCase();
            const mountFromTree = openAuthBtn.closest('[data-cclub-mount]');
            const openMid = (
              (mountFromTree && mountFromTree.getAttribute('data-cclub-mount')) ||
              openAuthBtn.getAttribute('data-cclub-mount') ||
              openAuthBtn.getAttribute('data-cclub-iwt-mount') ||
              ''
            ).trim();
            const openReg = openMid && window.__cclubIwtOpens && window.__cclubIwtOpens[openMid];
            if (openReg) {
              if (authKind === 'signin') openReg.openSignin();
              else openReg.openSignup();
              return;
            }
            let sectionFromMid = '';
            if (openMid.indexOf('cclub-iwt-mount-') === 0) {
              sectionFromMid = openMid.slice('cclub-iwt-mount-'.length);
            }
            if (authKind === 'signin') {
              if (sectionFromMid && mountRegistryOpenSignin(null, sectionFromMid)) return;
              mountRegistryOpenSignin(null, null);
            } else {
              if (sectionFromMid && mountRegistryOpenSignup(null, sectionFromMid)) return;
              mountRegistryOpenSignup(null, null);
            }
            return;
          }
  
          const su = e.target.closest('.cclub-auth-open-signup, .wc-cclub-signup');
          if (su) {
            e.preventDefault();
            const midSu = resolveMountIdFromEl(su);
            if (midSu && window.__cclubIwtOpens && window.__cclubIwtOpens[midSu]) {
              window.__cclubIwtOpens[midSu].openSignup();
              return;
            }
            mountRegistryOpenSignup(null, null);
            return;
          }
  
          const si = e.target.closest('.cclub-auth-open-signin, .wc-cclub-signin');
          if (si) {
            e.preventDefault();
            const midSi = resolveMountIdFromEl(si);
            if (midSi && window.__cclubIwtOpens && window.__cclubIwtOpens[midSi]) {
              window.__cclubIwtOpens[midSi].openSignin();
              return;
            }
            mountRegistryOpenSignin(null, null);
            return;
          }
        },
        true
      );
    }
  
    function wireEscapeOnce() {
      if (window.__cclubIwtEscape) return;
      window.__cclubIwtEscape = true;
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        const o = window.__cclubIwtOpens;
        if (!o) return;
        for (const k in o) {
          if (!Object.prototype.hasOwnProperty.call(o, k)) continue;
          const reg = o[k];
          const m = reg.mount;
          if (!m) continue;
          const fm = m.querySelector('[data-iwt="forgot-modal"]');
          if (fm && !fm.classList.contains('hidden') && reg.closeForgotModal) {
            e.preventDefault();
            reg.closeForgotModal();
            return;
          }
        }
        for (const k2 in o) {
          if (!Object.prototype.hasOwnProperty.call(o, k2)) continue;
          const m2 = o[k2].mount;
          if (!m2) continue;
          const su = m2.querySelector('[data-iwt="signup-overlay"]');
          const si = m2.querySelector('[data-iwt="signin-overlay"]');
          const open =
            (su && !su.classList.contains('hidden')) || (si && !si.classList.contains('hidden'));
          if (open) {
            e.preventDefault();
            o[k2].closeAll();
            return;
          }
        }
      });
    }
  
    function boot() {
      wireGlobalClickOnce();
      wireEscapeOnce();
      wireLegacyDispatchEventsOnce();
      document.querySelectorAll('.cclub-iwt-mount').forEach(bindMount);
    }
  
    window.cclubIwtAuthBoot = boot;
  
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  })();
  